import React, { useState, useRef, useEffect, useMemo } from 'react';
import { BrainElectricity, Xmark, NavArrowUp, NavArrowLeft } from 'iconoir-react';
import { C, FONT_BODY } from '../theme';
import { buildGeminiContext, buildFreeTextContext } from '../gemini';
import { supabase } from '../supabase';
import { calcAvg1RM, calcVolume, calcAvgRPE, filterWarmupSets } from '../science';

function buildChartData(exName, sessions) {
  const occ = [];
  for (const sess of [...sessions].sort((a, b) => a.date.localeCompare(b.date))) {
    for (const ex of sess.exercises) {
      if (ex.name.toLowerCase().trim() === exName.toLowerCase().trim()) {
        occ.push({ date: sess.date, ex });
      }
    }
  }
  return occ.map(o => {
    const working = filterWarmupSets(o.ex.sets);
    const vs = working.filter(s => Number(s.reps) > 0);
    const weights = working.map(s => parseFloat(s.weight)).filter(w => w > 0);
    const repsTotal = vs.reduce((a, s) => a + Number(s.reps), 0);
    return {
      date: o.date.slice(5),
      rm1: Math.round(calcAvg1RM(o.ex.sets) * 10) / 10,
      volume: Math.round(calcVolume(o.ex.sets)),
      rpe: calcAvgRPE(o.ex.sets) !== null ? Math.round(calcAvgRPE(o.ex.sets) * 10) / 10 : null,
      reps: vs.length ? Math.round(vs.reduce((a, s) => a + Number(s.reps), 0) / vs.length * 10) / 10 : null,
      repsTotal: repsTotal || null,
      peso: weights.length ? weights[0] : null,
    };
  });
}

export const STATO_COLORS = {
  'progressione': { bg: '#E8FAF0', color: '#34C759', border: '#A8E8BF' },
  'pr':           { bg: '#E5F1FF', color: '#007AFF', border: '#A8CBFF' },
  'stallo':       { bg: '#FFF4E0', color: '#FF9500', border: '#FFD080' },
  'regressione':  { bg: '#FFECEB', color: '#FF3B30', border: '#FFB3AF' },
};

function statoStyle(stato) {
  const key = (stato || '').toLowerCase().trim();
  return STATO_COLORS[key] || { bg: C.fill, color: C.subtle, border: C.fill };
}

function ExpandSection({ open, children }) {
  const [visible, setVisible] = useState(open);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setClosing(false);
    } else if (visible) {
      setClosing(true);
      const t = setTimeout(() => { setVisible(false); setClosing(false); }, 320);
      return () => clearTimeout(t);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;
  return (
    <div style={{
      overflow: 'hidden',
      maxHeight: closing ? 0 : 2000,
      transition: closing
        ? 'max-height 0.32s cubic-bezier(0.4,0,1,1)'
        : 'max-height 0.42s cubic-bezier(0.22,1,0.36,1)',
      willChange: 'max-height',
    }}>
      {children}
    </div>
  );
}

function getRawSets(exName, sessions, sessionIndex) {
  const matching = [...sessions]
    .filter(s => s.exercises.some(e => e.name.toLowerCase().trim() === exName.toLowerCase().trim()))
    .sort((a, b) => b.date.localeCompare(a.date));
  const sess = matching[sessionIndex];
  if (!sess) return { sets: [], date: null };
  const ex = sess.exercises.find(e => e.name.toLowerCase().trim() === exName.toLowerCase().trim());
  return { sets: ex ? filterWarmupSets(ex.sets) : [], date: sess.date };
}

function calcSlope(cd) {
  const pts = cd.filter(d => d.peso != null);
  if (pts.length < 2) return null;
  const n = pts.length;
  const meanI = (n - 1) / 2;
  const meanP = pts.reduce((a, d) => a + d.peso, 0) / n;
  const num = pts.reduce((a, d, i) => a + (i - meanI) * (d.peso - meanP), 0);
  const den = pts.reduce((a, _, i) => a + (i - meanI) ** 2, 0);
  return den === 0 ? 0 : num / den;
}

function SessionMatrix({ exName, sessions, cd }) {
  const lastData = useMemo(() => getRawSets(exName, sessions, 0), [exName, sessions]);
  const prevData = useMemo(() => getRawSets(exName, sessions, 1), [exName, sessions]);
  const slope = useMemo(() => calcSlope(cd), [cd]);

  const lastSets = lastData.sets.filter(s => Number(s.reps) > 0);
  const prevSets = prevData.sets.filter(s => Number(s.reps) > 0);
  const lastTotal = lastSets.reduce((a, s) => a + Number(s.reps), 0);
  const prevTotal = prevSets.reduce((a, s) => a + Number(s.reps), 0);
  const lastVol = Math.round(lastSets.reduce((a, s) => a + (parseFloat(s.weight) || 0) * (Number(s.reps) || 0), 0));
  const prevVol = Math.round(prevSets.reduce((a, s) => a + (parseFloat(s.weight) || 0) * (Number(s.reps) || 0), 0));

  const [dispLast, setDispLast] = useState(0);
  const [dispPrev, setDispPrev] = useState(0);
  const [dispLastVol, setDispLastVol] = useState(0);
  const [dispPrevVol, setDispPrevVol] = useState(0);
  useEffect(() => {
    if (!lastTotal) return;
    let f = 0;
    const iv = setInterval(() => { f++; setDispLast(Math.round(lastTotal * f / 20)); if (f >= 20) clearInterval(iv); }, 20);
    return () => clearInterval(iv);
  }, [lastTotal]);
  useEffect(() => {
    if (!prevTotal) return;
    let f = 0;
    const iv = setInterval(() => { f++; setDispPrev(Math.round(prevTotal * f / 20)); if (f >= 20) clearInterval(iv); }, 20);
    return () => clearInterval(iv);
  }, [prevTotal]);
  useEffect(() => {
    if (!lastVol) return;
    let f = 0;
    const iv = setInterval(() => { f++; setDispLastVol(Math.round(lastVol * f / 20)); if (f >= 20) clearInterval(iv); }, 20);
    return () => clearInterval(iv);
  }, [lastVol]);
  useEffect(() => {
    if (!prevVol) return;
    let f = 0;
    const iv = setInterval(() => { f++; setDispPrevVol(Math.round(prevVol * f / 20)); if (f >= 20) clearInterval(iv); }, 20);
    return () => clearInterval(iv);
  }, [prevVol]);

  const maxRows = Math.max(lastSets.length, prevSets.length);
  if (maxRows === 0) return null;

  const fmtLong = iso => {
    if (!iso) return '';
    const mesi = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
    const p = iso.split('-');
    const m = parseInt(p[p.length - 2]), d = parseInt(p[p.length - 1]);
    return (m >= 1 && m <= 12) ? `${d} ${mesi[m - 1]}` : iso.slice(5);
  };
  const lastDate = fmtLong(lastData.date);
  const prevDate = fmtLong(prevData.date);

  let slopeIcon = '→';
  let slopeColor = C.subtle;
  let slopeText = 'stabile';
  if (slope !== null) {
    const s = Math.round(Math.abs(slope) * 10) / 10;
    if (slope > 0.3) { slopeIcon = '↗'; slopeColor = C.green; slopeText = `+${s} kg/ses`; }
    else if (slope < -0.3) { slopeIcon = '↘'; slopeColor = C.red; slopeText = `-${s} kg/ses`; }
  }

  return (
    <div style={{ marginBottom: 12 }}>
      {/* Intestazioni colonne */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginBottom: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.text, letterSpacing: 0.1, fontFamily: FONT_BODY }}>
          Ultima {lastDate}
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.subtle, letterSpacing: 0.1, fontFamily: FONT_BODY, paddingLeft: 12 }}>
          Precedente {prevDate}
        </div>
      </div>

      {/* Righe serie */}
      {Array.from({ length: maxRows }).map((_, i) => {
        const ls = lastSets[i];
        const ps = prevSets[i];
        return (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginBottom: 3 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, borderRight: '0.5px solid rgba(60,60,67,0.10)', paddingRight: 10 }}>
              <span style={{ fontSize: 11, color: C.subtle, fontFamily: FONT_BODY, minWidth: 18 }}>S{i + 1}</span>
              {ls ? (
                <span style={{ fontSize: 13, fontWeight: 500, color: C.text, fontFamily: FONT_BODY }}>
                  {parseFloat(ls.weight) > 0 ? `${ls.weight} kg` : ''}{parseFloat(ls.weight) > 0 && Number(ls.reps) > 0 ? ' × ' : ''}{Number(ls.reps) > 0 ? `${ls.reps}` : ''}
                </span>
              ) : null}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, paddingLeft: 12 }}>
              <span style={{ fontSize: 11, color: C.subtle, fontFamily: FONT_BODY, minWidth: 18 }}>S{i + 1}</span>
              {ps ? (
                <span style={{ fontSize: 13, fontWeight: 500, color: C.subtle, fontFamily: FONT_BODY }}>
                  {parseFloat(ps.weight) > 0 ? `${ps.weight} kg` : ''}{parseFloat(ps.weight) > 0 && Number(ps.reps) > 0 ? ' × ' : ''}{Number(ps.reps) > 0 ? `${ps.reps}` : ''}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}

      {/* Totali */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginTop: 8, paddingTop: 6, borderTop: '0.5px solid rgba(60,60,67,0.08)' }}>
        <div style={{ borderRight: '0.5px solid rgba(60,60,67,0.10)', paddingRight: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: FONT_BODY }}>{dispLast} rep</div>
          {lastVol > 0 && <div style={{ fontSize: 12, fontWeight: 500, color: C.subtle, fontFamily: FONT_BODY, marginTop: 2 }}>{dispLastVol} kg vol.</div>}
        </div>
        <div style={{ paddingLeft: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.subtle, fontFamily: FONT_BODY }}>{dispPrev} rep</div>
          {prevVol > 0 && <div style={{ fontSize: 12, fontWeight: 500, color: C.subtle, fontFamily: FONT_BODY, marginTop: 2, opacity: 0.7 }}>{dispPrevVol} kg vol.</div>}
        </div>
      </div>

      {/* Slope peso */}
      {slope !== null && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '0.5px solid rgba(60,60,67,0.08)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: slopeColor, fontFamily: FONT_BODY }}>{slopeIcon} {slopeText}</span>
          <span style={{ fontSize: 12, color: C.subtle, fontFamily: FONT_BODY }}>tendenza peso</span>
        </div>
      )}
    </div>
  );
}

// Grafico SVG puro — nessuna libreria esterna
function SvgExerciseChart({ cd }) {
  const [metric, setMetric] = useState('peso');
  const [hover, setHover] = useState(null);

  const allMetrics = [
    { id: 'peso',      label: 'Carico',  color: '#30B0C7', unit: 'kg' },
    { id: 'rm1',       label: '1RM',     color: '#007AFF', unit: 'kg' },
    { id: 'repsTotal', label: 'Reps',    color: '#AF52DE', unit: '' },
    { id: 'volume',    label: 'Volume',  color: '#34C759', unit: '' },
  ];
  const metrics = allMetrics.filter(m => cd.some(d => d[m.id] != null));
  const current = metrics.find(m => m.id === metric) || metrics[0];
  if (!current) return null;

  const data = cd.map(d => ({ date: d.date, v: d[current.id] })).filter(d => d.v != null);
  if (data.length < 2) return null;

  // Dimensioni fisse — il viewBox scala con preserveAspectRatio
  const W = 280, H = 72, PL = 28, PR = 10, PT = 8, PB = 18;
  const iW = W - PL - PR, iH = H - PT - PB;
  const vals = data.map(d => d.v);
  const vMin = Math.min(...vals), vMax = Math.max(...vals);
  const span = vMax - vMin || 1;
  const yLo = vMin - span * 0.12, yHi = vMax + span * 0.18;
  const yRange = yHi - yLo;
  const xStep = iW / (data.length - 1);
  const toX = i => PL + i * xStep;
  const toY = v => PT + iH * (1 - (v - yLo) / yRange);

  // Catmull-Rom → Bezier per curve organiche
  const smoothPath = data.reduce((acc, d, i) => {
    const x = toX(i), y = toY(d.v);
    if (i === 0) return `M${x.toFixed(1)},${y.toFixed(1)}`;
    const p0 = data[Math.max(0, i - 2)], p1 = data[i - 1], p2 = d, p3 = data[Math.min(data.length - 1, i + 1)];
    const t = 0.4;
    const cp1x = toX(i - 1) + (toX(i) - toX(Math.max(0, i - 2))) * t / 2;
    const cp1y = toY(p1.v) + (toY(p2.v) - toY(p0.v)) * t / 2;
    const cp2x = toX(i) - (toX(Math.min(data.length - 1, i + 1)) - toX(i - 1)) * t / 2;
    const cp2y = toY(p2.v) - (toY(p3.v) - toY(p1.v)) * t / 2;
    return acc + ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${x.toFixed(1)},${y.toFixed(1)}`;
  }, '');

  const baseY = PT + iH;
  const areaPath = `${smoothPath} L${toX(data.length - 1).toFixed(1)},${baseY} L${toX(0).toFixed(1)},${baseY} Z`;

  const fmtY = v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v >= 100 ? Math.round(v) : v % 1 === 0 ? v : v.toFixed(1);
  const fmtDate = s => { const p = s.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}` : s; };
  const gradId = `g-${current.id}`;
  const clipId = `cl-${current.id}`;

  return (
    <div>
      {/* Segmented control */}
      {metrics.length > 1 && (
        <div style={{
          display: 'flex', background: 'rgba(120,120,128,0.10)', borderRadius: 7,
          padding: '2px', gap: 0, marginBottom: 10,
        }}>
          {metrics.map(m => {
            const sel = metric === m.id;
            return (
              <button key={m.id} onClick={() => { setMetric(m.id); setHover(null); }} style={{
                flex: 1, padding: '4px 0', border: 'none', borderRadius: 5,
                background: sel ? '#fff' : 'transparent',
                color: sel ? C.text : C.subtle,
                fontSize: 11, fontWeight: sel ? 600 : 400,
                cursor: 'pointer', fontFamily: FONT_BODY,
                boxShadow: sel ? '0 1px 2px rgba(0,0,0,0.09)' : 'none',
                transition: 'background 0.15s, box-shadow 0.15s',
                WebkitTapHighlightColor: 'transparent',
              }}>{m.label}</button>
            );
          })}
        </div>
      )}

      {/* Chart */}
      <div style={{ position: 'relative', width: '100%' }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          style={{ display: 'block', width: '100%', height: 'auto', overflow: 'hidden' }}
          onMouseLeave={() => setHover(null)}
          onTouchEnd={() => setHover(null)}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={current.color} stopOpacity="0.18" />
              <stop offset="100%" stopColor={current.color} stopOpacity="0" />
            </linearGradient>
            <clipPath id={clipId}>
              <rect x={PL} y={PT} width={iW} height={iH + 1} />
            </clipPath>
          </defs>

          {/* Y axis labels + grid lines (solo min e max) */}
          {[vMin, vMax].map((v, ti) => {
            const y = toY(v);
            return (
              <g key={ti}>
                <line x1={PL} y1={y} x2={W - PR} y2={y}
                  stroke="rgba(60,60,67,0.08)" strokeWidth="0.5" strokeDasharray={ti === 0 ? '0' : '3 3'} />
                <text x={PL - 4} y={y + 3.5} textAnchor="end"
                  fontSize="8" fill={C.subtle} fontFamily={FONT_BODY}>
                  {fmtY(v)}
                </text>
              </g>
            );
          })}

          {/* Area fill clippata */}
          <path d={areaPath} fill={`url(#${gradId})`} clipPath={`url(#${clipId})`} />

          {/* Line */}
          <path d={smoothPath} fill="none" stroke={current.color}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            clipPath={`url(#${clipId})`} />

          {/* X labels primo e ultimo */}
          {[0, data.length - 1].map(i => (
            <text key={i} x={toX(i)} y={H - 3} textAnchor={i === 0 ? 'start' : 'end'}
              fontSize="8" fill={C.subtle} fontFamily={FONT_BODY}>
              {fmtDate(data[i].date)}
            </text>
          ))}

          {/* Dot ultimo punto sempre visibile */}
          {(() => {
            const last = data[data.length - 1];
            const cx = toX(data.length - 1), cy = toY(last.v);
            return (
              <g>
                <circle cx={cx} cy={cy} r="3.5" fill={current.color} />
                <circle cx={cx} cy={cy} r="2" fill="#fff" />
              </g>
            );
          })()}

          {/* Hover targets */}
          {data.map((d, i) => {
            const cx = toX(i), cy = toY(d.v);
            const isHov = hover === i;
            return (
              <g key={i} onMouseEnter={() => setHover(i)} onTouchStart={e => { e.preventDefault(); setHover(i); }}>
                <circle cx={cx} cy={cy} r={14} fill="transparent" />
                {isHov && (
                  <g>
                    <circle cx={cx} cy={cy} r="4" fill={current.color} opacity="0.15" />
                    <circle cx={cx} cy={cy} r="2.5" fill="#fff" stroke={current.color} strokeWidth="1.5" />
                  </g>
                )}
              </g>
            );
          })}

          {/* Tooltip */}
          {hover !== null && (() => {
            const d = data[hover];
            const tx = toX(hover), ty = toY(d.v);
            const label = `${d.v}${current.unit}`;
            const bw = Math.max(36, label.length * 6.5 + 16), bh = 20, br = 5;
            const bx = Math.min(Math.max(tx - bw / 2, PL), W - PR - bw);
            const by = Math.max(PT - 2, ty - bh - 7);
            return (
              <g>
                <rect x={bx} y={by} width={bw} height={bh} rx={br} fill="#1C1C1E" opacity="0.9" />
                <text x={bx + bw / 2} y={by + 13.5} textAnchor="middle"
                  fontSize="9.5" fontWeight="600" fill="#fff" fontFamily={FONT_BODY}>
                  {label}
                </text>
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
}

function ExerciseCard({ exName, stato, analisi, causa, obiettivo, sessions, cardIndex }) {
  const cd = useMemo(() => buildChartData(exName, sessions), [exName, sessions]);
  const hasChart = cd.length >= 2;
  const hasConfronto = cd.length >= 2;
  const st = statoStyle(stato);
  const [activeTab, setActiveTab] = useState(null); // null | 'analisi' | 'confronto' | 'grafici'

  const last = cd.length >= 1 ? cd[cd.length - 1] : null;
  const prev = cd.length >= 2 ? cd[cd.length - 2] : null;
  const deltaPeso = last && prev && last.peso != null && prev.peso != null ? Math.round((last.peso - prev.peso) * 10) / 10 : null;
  const deltaReps = last && prev && last.repsTotal != null && prev.repsTotal != null ? last.repsTotal - prev.repsTotal : null;
  const deltaVol = last && prev && prev.volume > 0 ? Math.round((last.volume - prev.volume) / prev.volume * 100) : null;
  const prevDate = prev?.date ? (() => {
    const parts = prev.date.split('-');
    const m = parseInt(parts[parts.length - 2]);
    const d = parseInt(parts[parts.length - 1]);
    const mesi = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
    if (!m || !d || m < 1 || m > 12) return null;
    return `${d} ${mesi[m-1]}`;
  })() : null;

  const delay = `${(cardIndex || 0) * 0.07}s`;
  const tabs = [
    analisi ? { id: 'analisi', label: 'Analisi' } : null,
    hasConfronto ? { id: 'confronto', label: 'Confronto' } : null,
    hasChart ? { id: 'grafici', label: 'Grafici' } : null,
  ].filter(Boolean);

  return (
    <>
    <style>{`@keyframes gpCardIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    <div style={{
      marginBottom: obiettivo ? 0 : 12,
      background: '#ffffff',
      borderRadius: 14,
      boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
      overflow: 'hidden',
      animation: `gpCardIn 0.4s ${delay} ease-out both`,
    }}>

      {/* ZONA 1 — Nome + badge + hero metric */}
      <div style={{ padding: '14px 16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: last ? 10 : 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, letterSpacing: -0.3, lineHeight: 1.3, flex: 1 }}>
            {exName.split(' ').map(w => w.length > 0 ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w).join(' ')}
          </div>
          {stato && (
            <div style={{
              flexShrink: 0, fontSize: 11, fontWeight: 700, letterSpacing: 0.2,
              padding: '4px 10px', borderRadius: 20,
              background: st.bg, color: st.color,
            }}>{stato.charAt(0).toUpperCase() + stato.slice(1)}</div>
          )}
        </div>

        {last && last.peso != null && (
          <div style={{ fontSize: 28, fontWeight: 800, color: C.text, letterSpacing: -0.8, lineHeight: 1, fontFamily: FONT_BODY, marginBottom: 5 }}>
            {last.peso}<span style={{ fontSize: 14, fontWeight: 500, color: C.subtle, marginLeft: 3 }}>kg</span>
          </div>
        )}

        {/* Riga secondaria: reps + delta pills */}
        {last && (last.repsTotal != null || deltaPeso !== null || deltaReps !== null || deltaVol !== null) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {last.repsTotal != null && (
              <span style={{ fontSize: 13, fontWeight: 500, color: C.muted, fontFamily: FONT_BODY }}>
                {last.repsTotal} rep tot.
              </span>
            )}
            {deltaPeso !== null && deltaPeso !== 0 && (
              <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: deltaPeso < 0 ? C.redL : C.greenL, color: deltaPeso < 0 ? C.red : C.green, fontFamily: FONT_BODY }}>
                {deltaPeso > 0 ? '+' : ''}{deltaPeso}kg
              </span>
            )}
            {deltaReps !== null && deltaReps !== 0 && (
              <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: deltaReps < 0 ? C.redL : C.greenL, color: deltaReps < 0 ? C.red : C.green, fontFamily: FONT_BODY }}>
                {deltaReps > 0 ? '+' : ''}{deltaReps} rep
              </span>
            )}
            {deltaVol !== null && deltaVol !== 0 && (
              <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: deltaVol < 0 ? C.redL : C.greenL, color: deltaVol < 0 ? C.red : C.green, fontFamily: FONT_BODY }}>
                {deltaVol > 0 ? '+' : ''}{deltaVol}% vol
              </span>
            )}
            {prevDate && <span style={{ fontSize: 11, color: C.subtle, fontFamily: FONT_BODY }}>vs {prevDate}</span>}
          </div>
        )}

      </div>

      {/* ZONA 2 — Segmented control monocolore + pannelli */}
      {tabs.length > 0 && (
        <div style={{ borderTop: '0.5px solid rgba(60,60,67,0.07)' }}>
          {/* Segmented control */}
          <div style={{ display: 'flex', background: 'rgba(120,120,128,0.08)', margin: '0 12px', borderRadius: 10, padding: 3, gap: 2, marginTop: 10, marginBottom: activeTab ? 0 : 10 }}>
            {tabs.map(t => {
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(activeTab === t.id ? null : t.id)}
                  style={{
                    flex: 1, padding: '6px 0', border: 'none', borderRadius: 8,
                    background: active ? '#fff' : 'transparent',
                    color: active ? C.text : C.subtle,
                    fontSize: 12, fontWeight: active ? 600 : 500,
                    cursor: 'pointer', fontFamily: FONT_BODY,
                    boxShadow: active ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                    transition: 'background 0.15s, color 0.15s, box-shadow 0.15s',
                    WebkitTapHighlightColor: 'transparent',
                  }}>{t.label}</button>
              );
            })}
          </div>

          <ExpandSection open={activeTab === 'analisi'}>
            <div style={{ padding: '12px 16px 14px', borderTop: '0.5px solid rgba(60,60,67,0.07)', marginTop: 10 }}>
              {analisi && <div style={{ fontSize: 13, color: C.text, lineHeight: 1.65, marginBottom: causa ? 10 : 0, fontFamily: FONT_BODY }}>{analisi}</div>}
              {causa && <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, paddingLeft: 10, borderLeft: `2px solid ${st.border}`, fontFamily: FONT_BODY }}>{causa}</div>}
            </div>
          </ExpandSection>
          <ExpandSection open={activeTab === 'confronto'}>
            <div style={{ padding: '12px 16px 14px', borderTop: '0.5px solid rgba(60,60,67,0.07)', marginTop: 10 }}>
              <SessionMatrix exName={exName} sessions={sessions} cd={cd} />
            </div>
          </ExpandSection>
          <ExpandSection open={activeTab === 'grafici'}>
            <div style={{ margin: '10px -1px 0', padding: '12px 16px 14px', borderTop: '0.5px solid rgba(60,60,67,0.07)' }}>
              <SvgExerciseChart cd={cd} />
            </div>
          </ExpandSection>
        </div>
      )}
    </div>
    {obiettivo && (
      <div style={{
        marginBottom: 12,
        marginTop: 0,
        background: 'rgba(0,122,255,0.06)',
        borderRadius: '0 0 12px 12px',
        borderTop: '1px dashed rgba(0,122,255,0.18)',
        padding: '10px 16px 11px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 9,
        animation: `gpCardIn 0.4s ${delay} ease-out both`,
      }}>
        <div style={{ marginTop: 2, width: 16, height: 16, borderRadius: 8, background: 'rgba(0,122,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M4.5 1v7M1 4.5h7" stroke="#007AFF" strokeWidth="1.6" strokeLinecap="round"/></svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.4, color: 'rgba(0,122,255,0.6)', textTransform: 'uppercase', marginBottom: 2, fontFamily: FONT_BODY }}>Prossima sessione</div>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#007AFF', lineHeight: 1.45, fontFamily: FONT_BODY }}>{obiettivo}</div>
        </div>
      </div>
    )}
    </>
  );
}

// CTA prescrizione separata — raccoglie tutti gli obiettivi e li mostra come lista
function PrescriptionCTA({ blocks }) {
  const items = blocks.filter(b => b.obiettivo);
  if (!items.length) return null;
  return (
    <div style={{
      marginBottom: 20,
      background: '#fff',
      borderRadius: 14,
      boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
      overflow: 'hidden',
    }}>
      <div style={{ padding: '12px 16px', borderBottom: '0.5px solid rgba(60,60,67,0.07)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.blue, flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: C.blue, fontFamily: FONT_BODY }}>Prossima sessione</span>
      </div>
      <div style={{ padding: '8px 16px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((b, i) => {
          const s = (b.stato || '').toLowerCase();
          const dotColor = s === 'progressione' || s === 'pr' ? C.green
            : s === 'regressione' ? C.red
            : '#FF9500';
          // Prima parola significativa del nome come etichetta corta
          const words = b.name.split(' ');
          const label = words.length <= 2 ? b.name : words.filter(w => w.length > 3).slice(0, 2).join(' ') || words.slice(0, 2).join(' ');
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor, marginTop: 5, flexShrink: 0 }} />
              <div style={{ flex: 1, fontSize: 13, color: C.text, lineHeight: 1.5, fontFamily: FONT_BODY }}>
                <span style={{ fontWeight: 600 }}>{label}:</span>{' '}
                <span style={{ color: C.subtle }}>{b.obiettivo}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ExerciseChartBlock({ exName, sessions }) {
  const cd = useMemo(() => buildChartData(exName, sessions), [exName, sessions]);
  if (cd.length < 2) return null;
  return (
    <div style={{
      marginBottom: 12,
      background: '#ffffff',
      borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
      padding: '14px 16px 14px',
    }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 12, letterSpacing: -0.3 }}>
        {exName.split(' ').map(w => w.length > 0 ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w).join(' ')}
      </div>
      <SvgExerciseChart cd={cd} />
    </div>
  );
}

const SECTION_STYLES = {
  '[verdetto]':               { color: '#34C759', label: 'Verdetto' },
  '[esercizi]':               { color: '#007AFF', label: 'Esercizi' },
  '[piano prossima sessione]':{ color: '#AF52DE', label: 'Piano prossima sessione' },
  '[alert]':                  { color: '#FF3B30', label: 'Alert' },
  '[deload]':                 { color: '#FF9500', label: 'Deload' },
};

function getSectionStyle(line) {
  const lower = line.toLowerCase().replace(/\*/g, '').trim();
  for (const [key, val] of Object.entries(SECTION_STYLES)) {
    if (lower.includes(key)) return val;
  }
  return null;
}

function renderInlineMarkdown(line, keyPrefix) {
  const clean = line.replace(/^[*-]\s/, '');
  return clean.split(/(\*\*[^*]+\*\*)/g).map((part, j) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-b${j}`} style={{ fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function AlertCard({ text, key: _key }) {
  const [visible, setVisible] = useState(true);
  const [expanded, setExpanded] = useState(false);
  if (!visible) return null;
  // Prima frase come sommario
  const firstSentence = text.split(/[.!?]/)[0].trim();
  const hasMore = text.length > firstSentence.length + 1;
  return (
    <div style={{
      marginBottom: 12, borderRadius: 10, background: '#FFF0EF',
      animation: 'alertBannerIn 0.3s ease-out both',
    }}>
      <style>{`@keyframes alertBannerIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px' }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            stroke="#FF3B30" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span style={{ fontSize: 13, color: C.text, fontFamily: FONT_BODY, lineHeight: 1.4, flex: 1 }}>
          {expanded ? text : firstSentence}{!expanded && hasMore ? '.' : ''}
        </span>
        {hasMore && (
          <button onClick={() => setExpanded(e => !e)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', flexShrink: 0,
            fontSize: 11, fontWeight: 600, color: '#FF3B30', fontFamily: FONT_BODY,
            WebkitTapHighlightColor: 'transparent',
          }}>{expanded ? 'Meno' : 'Altro'}</button>
        )}
        <button onClick={() => setVisible(false)} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0,
          WebkitTapHighlightColor: 'transparent', display: 'flex', alignItems: 'center',
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="rgba(120,120,128,0.5)" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
}


function renderTextLines(lines, keyPrefix) {
  return lines.map((line, i) => {
    if (line.trim() === '') return <div key={`${keyPrefix}-sp${i}`} style={{ height: 5 }} />;
    const isBullet = /^[*-]\s/.test(line);
    if (isBullet) {
      return (
        <div key={`${keyPrefix}-${i}`} style={{ display: 'flex', gap: 7, marginTop: 4, lineHeight: 1.55 }}>
          <span style={{ flexShrink: 0, color: C.subtle, marginTop: 1 }}>•</span>
          <span>{renderInlineMarkdown(line, `${keyPrefix}-${i}`)}</span>
        </div>
      );
    }
    return <div key={`${keyPrefix}-${i}`} style={{ marginTop: i > 0 ? 4 : 0, lineHeight: 1.6 }}>{renderInlineMarkdown(line, `${keyPrefix}-${i}`)}</div>;
  });
}

// Normalizza un nome esercizio: rimuove bold markdown, numerazione iniziale e whitespace extra.
// "**Panca Piana**" -> "Panca Piana"
// "1. Hack Squat"   -> "Hack Squat"
// "- Curl bilanciere" -> "Curl bilanciere"
function normalizeExerciseName(raw) {
  return raw
    .replace(/^\*{1,2}/, '').replace(/\*{1,2}$/, '') // rimuove ** o * iniziali/finali
    .replace(/^\d+\.\s*/, '')                          // rimuove "1. " iniziale
    .replace(/^[-•]\s*/, '')                           // rimuove "- " o "• " iniziale
    .trim();
}

// Restituisce true se la riga è un campo strutturato (Stato/Analisi/Causa/Obiettivo)
function isFieldLine(l) {
  return /^(Stato|Analisi|Causa|Obiettivo):/i.test(l);
}

// Restituisce true se la riga sembra un nome esercizio valido:
// - non è un campo strutturato
// - ha almeno 2 caratteri dopo normalizzazione
// - non è un testo generico molto lungo (>80 char indicano una frase, non un nome)
function looksLikeExerciseName(l) {
  if (isFieldLine(l)) return false;
  const normalized = normalizeExerciseName(l);
  return normalized.length >= 2 && normalized.length <= 80;
}

// Parse blocchi esercizio strutturati dal testo Gemini.
// Gestisce varianti comuni: bold markdown nel nome (**NOME**), numerazione (1. NOME),
// bullet iniziale (- NOME), righe di continuazione, e field lines senza current aperto.
export function parseExerciseBlocks(lines) {
  const blocks = [];
  let current = null;
  // Traccia l'ultimo campo aggiornato per appendere righe di continuazione
  let lastField = null;

  const flushCurrent = () => {
    if (current && (current.stato || current.analisi)) {
      blocks.push(current);
    }
    current = null;
    lastField = null;
  };

  for (const line of lines) {
    const l = line.trim();

    // Riga vuota: chiude il blocco corrente
    if (!l) {
      flushCurrent();
      continue;
    }

    const statoM = l.match(/^Stato:\s*(.*)/i);
    const analisiM = l.match(/^Analisi:\s*(.*)/i);
    const causaM = l.match(/^Causa:\s*(.*)/i);
    const obiettivoM = l.match(/^Obiettivo:\s*(.*)/i);

    if (statoM) {
      // Se arriva Stato: senza current aperto, crea un placeholder anonimo
      // (caso Gemini dimentica la riga del nome)
      if (!current) current = { name: '', stato: null, analisi: null, causa: null, obiettivo: null };
      current.stato = statoM[1].trim();
      lastField = 'stato';
      continue;
    }
    if (analisiM) {
      if (!current) current = { name: '', stato: null, analisi: null, causa: null, obiettivo: null };
      current.analisi = analisiM[1].trim();
      lastField = 'analisi';
      continue;
    }
    if (causaM) {
      if (!current) current = { name: '', stato: null, analisi: null, causa: null, obiettivo: null };
      current.causa = causaM[1].trim();
      lastField = 'causa';
      continue;
    }
    if (obiettivoM) {
      if (!current) current = { name: '', stato: null, analisi: null, causa: null, obiettivo: null };
      current.obiettivo = obiettivoM[1].trim();
      lastField = 'obiettivo';
      continue;
    }

    // Riga non strutturata: potrebbe essere nome esercizio o continuazione di campo
    if (current && lastField && current[lastField] !== null) {
      // Se il current ha gia almeno uno dei campi chiave (stato o analisi), considera
      // questa riga come continuazione del campo precedente, non come nuovo nome.
      // Evita di creare esercizi fantasma da righe di testo libero dopo i campi.
      if (current.stato || current.analisi) {
        current[lastField] += ' ' + l;
        continue;
      }
    }

    // A questo punto la riga e un candidato nome esercizio
    if (looksLikeExerciseName(l)) {
      flushCurrent();
      const name = normalizeExerciseName(l);
      current = { name, stato: null, analisi: null, causa: null, obiettivo: null };
      lastField = null;
    }
    // Ignora righe non riconoscibili (testo libero fuori dai blocchi)
  }

  flushCurrent();
  return blocks;
}

export function saveCoachAdvices(schedaName, blocks, rawText) {
  if (!schedaName || !blocks.length) return;
  try {
    const key = `gp_coach_advices_${schedaName}`;
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), advices: blocks, rawText: rawText || null }));
  } catch {}
}

export function getCoachAdvice(schedaName, exName) {
  if (!schedaName || !exName) return null;
  try {
    const raw = localStorage.getItem(`gp_coach_advices_${schedaName}`);
    if (!raw) return null;
    const { advices } = JSON.parse(raw);
    if (!Array.isArray(advices)) return null;
    const nameLower = exName.toLowerCase();
    return advices.find(a => {
      const aLower = (a.name || '').toLowerCase();
      if (aLower === nameLower) return true;
      const words = nameLower.split(/\s+/).filter(w => w.length > 2);
      return words.length > 0 && words.filter(w => aLower.includes(w)).length >= Math.min(2, words.length);
    }) || null;
  } catch { return null; }
}

function renderCoachOutput(text, charts, sessions, schedaName) {
  if (!text) return null;

  // Dividi il testo nelle grandi sezioni **[...]**
  const sectionRegex = /(\*\*\[.*?\]\*\*)/g;
  const parts = text.split(sectionRegex);

  // Estrai testo Alert prima di costruire il result, così può andare in cima
  let alertText = null;
  for (let pi = 0; pi < parts.length; pi++) {
    const part = parts[pi];
    if (getSectionStyle(part)?.label === 'Alert' && pi + 1 < parts.length) {
      alertText = parts[pi + 1].trim();
      break;
    }
  }

  const result = [];
  if (alertText) {
    result.push(<AlertCard key="alert-top" text={alertText} />);
  }

  for (let pi = 0; pi < parts.length; pi++) {
    const part = parts[pi];
    const sectionStyle = getSectionStyle(part);

    // Salta header Alert (contenuto già in cima) + header [Piano prossima sessione] (gestito inline) + header [Esercizi]
    if (sectionStyle) {
      if (sectionStyle.label === 'Alert') {
        pi++; // salta anche il contenuto
      }
      // [Piano prossima sessione]: skip — gestito da PrescriptionCTA
      else if (sectionStyle.label === 'Piano prossima sessione') {
        pi++; // salta anche il contenuto
      }
      // tutti gli altri header (Verdetto, Esercizi, Deload): skip silenzioso
      continue;
    }

    const isEserciziContent = pi > 0 && getSectionStyle(parts[pi - 1])?.label === 'Esercizi';

    if (isEserciziContent && charts && sessions) {
      // Prova a parsare i blocchi strutturati
      const lines = part.split('\n');
      const blocks = parseExerciseBlocks(lines);
      const usedNames = new Set();

      if (blocks.length > 0) {
        // Determina la scheda dalla sessione più recente nei charts
        const inferredScheda = schedaName || (() => {
          if (!sessions.length) return null;
          const sorted = [...sessions].sort((a, b) => b.date.localeCompare(a.date));
          return sorted.find(s => charts.some(ch => s.exercises.some(e => e.name === ch)))?.workoutName || null;
        })();
        saveCoachAdvices(inferredScheda, blocks, text);

        // Fuzzy match: per ogni block trova il chart con overlap massimo
        // Usa score = parole comuni / max(len_chart, len_block) — threshold 0.5
        const fuzzyScore = (a, b) => {
          if (a === b) return 1;
          const wa = a.split(/\s+/).filter(w => w.length > 2);
          const wb = b.split(/\s+/).filter(w => w.length > 2);
          if (!wa.length || !wb.length) return 0;
          const common = wa.filter(w => b.includes(w)).length + wb.filter(w => a.includes(w)).length;
          return common / (wa.length + wb.length);
        };

        blocks.forEach((block, bi) => {
          const blockLower = block.name.toLowerCase();
          // Trova il chart con score più alto tra quelli disponibili
          let bestChart = null, bestScore = 0;
          for (const exName of charts) {
            if (usedNames.has(exName)) continue;
            const score = fuzzyScore(exName.toLowerCase(), blockLower);
            if (score > bestScore) { bestScore = score; bestChart = exName; }
          }
          const matchedChart = bestScore >= 0.5 ? bestChart : null;
          if (matchedChart) usedNames.add(matchedChart);

          result.push(
            <ExerciseCard
              key={`ex-${bi}`}
              exName={matchedChart || block.name}
              stato={block.stato}
              analisi={block.analisi}
              causa={block.causa}
              obiettivo={block.obiettivo}
              sessions={sessions}
              cardIndex={bi}
            />
          );
        });

        // CTA prescrizione unificata dopo tutte le card
        result.push(<PrescriptionCTA key="prescription-cta" blocks={blocks} />);

        // Grafici non abbinati a nessun blocco
        if (charts) {
          for (const exName of charts) {
            if (!usedNames.has(exName)) {
              result.push(
                <ExerciseChartBlock key={`extra-${exName}`} exName={exName} sessions={sessions} />
              );
            }
          }
        }
      } else {
        // Fallback: nessun blocco parsato, mostra testo + grafici in fondo
        result.push(...renderTextLines(lines, `esercizi-${pi}`));
        if (charts) {
          for (const exName of charts) {
            result.push(<ExerciseChartBlock key={`fb-${exName}`} exName={exName} sessions={sessions} />);
          }
        }
      }
    } else {
      const lines = part.split('\n');
      result.push(...renderTextLines(lines, `p${pi}`));
    }
  }

  return result;
}

function Message({ role, text, charts, sessions, schedaName }) {
  const isUser = role === 'user';
  if (isUser) {
    return (
      <div style={{ marginBottom: 16, padding: '0 16px', display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{
          maxWidth: '82%', padding: '10px 15px',
          borderRadius: '18px 18px 4px 18px',
          background: C.blue,
          color: '#fff',
          fontSize: 15, lineHeight: 1.5, fontFamily: FONT_BODY,
        }}>
          {text}
        </div>
      </div>
    );
  }
  return (
    <div style={{
      marginBottom: 20, padding: '0 18px',
      color: C.text, fontSize: 15, lineHeight: 1.65, fontFamily: FONT_BODY,
    }}>
      {renderCoachOutput(text, charts, sessions, schedaName)}
    </div>
  );
}

function Chip({ label, onClick, disabled }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        padding: '8px 14px', borderRadius: 20, border: '1px solid rgba(120,120,128,0.22)',
        background: disabled ? 'rgba(120,120,128,0.05)' : 'rgba(255,255,255,0.9)',
        color: disabled ? C.subtle : C.text, fontSize: 13, fontWeight: 500, fontFamily: FONT_BODY,
        cursor: disabled ? 'default' : 'pointer', flexShrink: 0,
        WebkitTapHighlightColor: 'transparent', transition: 'opacity 0.15s',
      }}>
      {label}
    </button>
  );
}

function AnalysisLoadingScreen({ exNames, schedaName }) {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [barWidth, setBarWidth] = useState(0);

  const phases = React.useMemo(() => {
    const steps = [];
    // Fase 1: analisi esercizi (uno per uno, fino a 4)
    const exSlice = exNames.slice(0, 4);
    exSlice.forEach(ex => {
      const short = ex.length > 28 ? ex.slice(0, 26) + '...' : ex;
      steps.push({ text: `Analizzo ${short}`, icon: 'brain' });
    });
    // Fase 2: serie e carico
    steps.push({ text: 'Controllo i carichi e le serie', icon: 'bar' });
    // Fase 3: pattern
    steps.push({ text: 'Cerco pattern di progressione', icon: 'trend' });
    // Fase 4: metto insieme
    steps.push({ text: 'Metto insieme tutto...', icon: 'spark' });
    return steps;
  }, [exNames]);

  useEffect(() => {
    // Avanza le fasi ogni 1.6s
    const interval = setInterval(() => {
      setPhaseIdx(prev => (prev < phases.length - 1 ? prev + 1 : prev));
    }, 1600);
    return () => clearInterval(interval);
  }, [phases.length]);

  // Progress bar: avanza gradualmente, si ferma a 90% finché non arriva il risultato
  useEffect(() => {
    const totalDuration = phases.length * 1600;
    const target = 90;
    const step = target / (totalDuration / 80);
    const t = setInterval(() => {
      setBarWidth(prev => {
        const next = prev + step;
        return next >= target ? target : next;
      });
    }, 80);
    return () => clearInterval(t);
  }, [phases.length]);

  const phase = phases[phaseIdx] || phases[phases.length - 1];

  const icons = {
    brain: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M11 3C8.5 3 6.5 4.8 6.5 7c0 .6.1 1.1.4 1.6C5.6 9.1 5 10 5 11c0 1 .5 1.9 1.3 2.4-.1.3-.3.6-.3 1 0 1.7 1.3 3 3 3h4c1.7 0 3-1.3 3-3 0-.4-.1-.7-.3-1 .8-.5 1.3-1.4 1.3-2.4 0-1-.6-1.9-1.4-2.4.3-.5.4-1 .4-1.6C16.5 4.8 13.5 3 11 3Z" stroke={C.blue} strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
        <line x1="11" y1="8" x2="11" y2="14" stroke={C.blue} strokeWidth="1.4" strokeLinecap="round"/>
        <line x1="8.5" y1="11" x2="13.5" y2="11" stroke={C.blue} strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    bar: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <rect x="3" y="14" width="4" height="5" rx="1" fill={C.blue} fillOpacity="0.4"/>
        <rect x="9" y="10" width="4" height="9" rx="1" fill={C.blue} fillOpacity="0.65"/>
        <rect x="15" y="6" width="4" height="13" rx="1" fill={C.blue}/>
      </svg>
    ),
    trend: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <polyline points="3,17 8,11 13,13 19,6" stroke={C.blue} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <circle cx="19" cy="6" r="2.5" fill={C.blue}/>
      </svg>
    ),
    spark: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M11 2L13.5 8.5L20 11L13.5 13.5L11 20L8.5 13.5L2 11L8.5 8.5Z" stroke={C.blue} strokeWidth="1.6" strokeLinejoin="round" fill={`${C.blue}18`}/>
      </svg>
    ),
  };

  return (
    <div style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
      {/* Icona */}
      <div style={{
        width: 64, height: 64, borderRadius: 20,
        background: `${C.blue}10`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 28,
      }}>
        <BrainElectricity width={30} height={30} color={C.blue} strokeWidth={1.6} />
      </div>

      {/* Titolo */}
      <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8, textAlign: 'center' }}>
        Analisi in corso
      </div>

      {/* Testo fase — cambia con fade */}
      <div key={phaseIdx} style={{
        fontSize: 14, color: C.subtle, marginBottom: 28, textAlign: 'center',
        display: 'flex', alignItems: 'center', gap: 7,
        animation: 'coachPhaseIn 0.35s cubic-bezier(0.22,1,0.36,1) both',
        minHeight: 22,
      }}>
        {icons[phase.icon]}
        <span>{phase.text}</span>
      </div>

      {/* Progress bar */}
      <div style={{ width: '100%', height: 4, borderRadius: 4, background: `${C.blue}14`, overflow: 'hidden', marginBottom: 10 }}>
        <div style={{
          height: '100%', borderRadius: 4, background: C.blue,
          width: `${barWidth}%`,
          transition: 'width 0.4s cubic-bezier(0.25,0.46,0.45,0.94)',
        }} />
      </div>

      {/* Label scheda */}
      {schedaName && (
        <div style={{ fontSize: 12, color: C.subtle, opacity: 0.6, marginTop: 4 }}>
          {schedaName}
        </div>
      )}

      <style>{`
        @keyframes coachPhaseIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export function CoachChatPage({ db, autoSchedaName, onConsumeAutoScheda, onStartSession, onAvviaMode, onBack }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [coachError, setCoachError] = useState(null); // { type: 'overload'|'generic', retry: fn }
  const [exSubChips, setExSubChips] = useState(null);
  const [startSessionName, setStartSessionName] = useState(null);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const autoSchedaHandled = useRef(false);
  const isAvviaMode = useRef(false);

  // Keep the ref in sync with startSessionName so the scroll effect can read it
  // without being listed as a dependency.
  useEffect(() => {
    isAvviaMode.current = !!startSessionName;
  });

  useEffect(() => {
    // In avviaMode (analisi scheda), never auto-scroll to bottom.
    // Auto-scroll is only useful in free-chat mode so the user sees the reply.
    if (isAvviaMode.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
  }, [messages, loading]);

  useEffect(() => {
    onAvviaMode?.(!!startSessionName);
    return () => { if (startSessionName) onAvviaMode?.(false); };
  }, [startSessionName, onAvviaMode]);

  // Reset guard when a new schedaName arrives + scroll to top
  useEffect(() => {
    autoSchedaHandled.current = false;
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [autoSchedaName]);

  useEffect(() => {
    const schedaName = autoSchedaName?.name ?? autoSchedaName;
    if (!schedaName || !db.sessions.length) return;
    if (autoSchedaHandled.current) return;
    autoSchedaHandled.current = true;
    setStartSessionName(schedaName);
    // Se c'è analisi in cache valida (salvata dopo l'ultima sessione), la carica senza chiamare Gemini
    try {
      const raw = localStorage.getItem(`gp_coach_advices_${schedaName}`);
      if (raw) {
        const { advices, rawText, ts } = JSON.parse(raw);
        const lastOfScheda = [...db.sessions].sort((a, b) => b.date.localeCompare(a.date)).find(s => s.workoutName === schedaName);
        // Invalida cache se salvata prima dell'ultima sessione
        const lastSessionTs = lastOfScheda ? new Date(lastOfScheda.date).getTime() : 0;
        if (ts && lastSessionTs && ts < lastSessionTs) {
          localStorage.removeItem(`gp_coach_advices_${schedaName}`);
          throw new Error('cache stale');
        }
        if (Array.isArray(advices) && advices.length) {
          const sessionExNames = lastOfScheda ? [...new Set(lastOfScheda.exercises.map(e => e.name))] : [];
          // Abbina i nomi Gemini ai nomi esatti delle sessioni via fuzzy match
          const chartExs = advices.map(a => {
            const aLower = (a.name || '').toLowerCase();
            const matched = sessionExNames.find(sn => {
              const words = sn.toLowerCase().split(/\s+/).filter(w => w.length > 2);
              return words.length > 0 && words.filter(w => aLower.includes(w)).length >= Math.min(2, words.length);
            });
            return matched || a.name;
          }).filter(Boolean);
          // Usa rawText se disponibile, altrimenti ricostruisce dal blocchi strutturati
          const text = rawText || [
            '**[ESERCIZI]**',
            advices.map(a => [
              a.name,
              a.stato ? `Stato: ${a.stato}` : '',
              a.analisi ? `Analisi: ${a.analisi}` : '',
              a.causa ? `Causa: ${a.causa}` : '',
              a.obiettivo ? `Obiettivo: ${a.obiettivo}` : '',
            ].filter(Boolean).join('\n')).join('\n\n'),
          ].join('\n');
          setMessages([
            { role: 'user', text: `Analizza scheda: ${schedaName}` },
            { role: 'coach', text, charts: chartExs, schedaName },
          ]);
          onConsumeAutoScheda?.();
          return;
        }
      }
    } catch {}
    handleChipSessionForScheda(schedaName);
    onConsumeAutoScheda?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSchedaName, db.sessions]);

  const lastSchedaName = useMemo(() => {
    if (!db.sessions.length) return null;
    const sorted = [...db.sessions].sort((a, b) => b.date.localeCompare(a.date));
    return sorted[0].workoutName || null;
  }, [db.sessions]);

  const exercisesInLastScheda = useMemo(() => {
    if (!db.sessions.length) return [];
    const sorted = [...db.sessions].sort((a, b) => b.date.localeCompare(a.date));
    const last = sorted[0];
    return [...new Set(last.exercises.map(e => e.name))].slice(0, 8);
  }, [db.sessions]);

  async function sendToCoach(prompt, displayText, system, chartExercises, schedaName) {
    const userMsg = displayText || prompt;
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);
    setCoachError(null);
    const retry = () => sendToCoach(prompt, displayText, system, chartExercises, schedaName);
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setCoachError({ type: 'generic', retry });
    }, 30000);
    try {
      const { data, error } = await supabase.functions.invoke('coach-analysis', {
        body: system ? { prompt, system } : { prompt },
      });
      clearTimeout(timeoutId);
      const isOverload = data?.error === 'upstream non disponibile' || error?.message?.includes('503') || error?.message?.includes('non-2xx');
      if (error || !data?.text) {
        console.error('[CoachChat] invoke error:', error, 'data:', data);
        setMessages(prev => prev.filter(m => !(m.role === 'user' && m.text === userMsg) || prev.indexOf(m) < prev.length - 1));
        setCoachError({ type: isOverload ? 'overload' : 'generic', retry });
      } else {
        if (isAvviaMode.current) {
          setShowComplete(true);
          await new Promise(r => setTimeout(r, 1800));
          setShowComplete(false);
        }
        setMessages(prev => [...prev, { role: 'coach', text: data.text, charts: chartExercises || null, schedaName: schedaName || null }]);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('[CoachChat] error:', err);
      setCoachError({ type: 'generic', retry });
    }
    setLoading(false);
  }

  function handleChipSession() {
    if (!db.sessions.length) return;
    const sorted = [...db.sessions].sort((a, b) => b.date.localeCompare(a.date));
    const lastSess = sorted[0];
    const chartExs = lastSess ? [...new Set(lastSess.exercises.map(e => e.name))] : [];
    const { prompt, system } = buildGeminiContext(db.sessions, 'session');
    const label = lastSchedaName ? `Analizza ultima sessione: ${lastSchedaName}` : 'Analizza ultima sessione';
    sendToCoach(prompt, label, system, chartExs, lastSchedaName);
    setExSubChips(null);
  }

  function handleChipSessionForScheda(schedaName) {
    if (!db.sessions.length) return;
    const sorted = [...db.sessions].sort((a, b) => b.date.localeCompare(a.date));
    const lastOfScheda = sorted.find(s => s.workoutName === schedaName);
    const chartExs = lastOfScheda ? [...new Set(lastOfScheda.exercises.map(e => e.name))] : [];
    const { prompt, system } = buildGeminiContext(db.sessions, 'session', { schedaName });
    sendToCoach(prompt, `Analizza scheda: ${schedaName}`, system, chartExs, schedaName);
    setExSubChips(null);
  }

  function handleChipExercise() {
    if (!exercisesInLastScheda.length) return;
    setExSubChips(exercisesInLastScheda);
  }

  function handleSelectExercise(exName) {
    const { prompt, system } = buildGeminiContext(db.sessions, 'exercise', { exerciseName: exName });
    sendToCoach(prompt, `Progressione: ${exName}`, system, [exName], lastSchedaName);
    setExSubChips(null);
  }

  function handleChipDeload() {
    const { prompt, system } = buildGeminiContext(db.sessions, 'deload');
    sendToCoach(prompt, 'Serve un deload?', system, null, null);
    setExSubChips(null);
  }

  function handleSend() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setExSubChips(null);
    const { prompt, system } = buildFreeTextContext(db.sessions, text);
    const allNames = [...new Set(db.sessions.map(s => s.workoutName).filter(Boolean))];
    const msgLower = text.toLowerCase();
    const matchedScheda = allNames.find(name =>
      name.toLowerCase().split(/\s+|[^a-z]/i).some(word => word.length > 3 && msgLower.includes(word))
    );
    let chartExs = null;
    if (matchedScheda) {
      const sorted = [...db.sessions].sort((a, b) => b.date.localeCompare(a.date));
      const lastOfScheda = sorted.find(s => s.workoutName === matchedScheda);
      if (lastOfScheda) chartExs = [...new Set(lastOfScheda.exercises.map(e => e.name))];
    }
    sendToCoach(prompt, text, system, chartExs, matchedScheda || lastSchedaName);
  }

  const noData = !db.sessions.length;
  // Modalità "avvia": nessuna chat/pillole, solo output + CTA
  const avviaMode = !!startSessionName;
  const hasCoachReply = messages.some(m => m.role === 'coach');

  // Schermata loader / completamento / errore in avviaMode
  if (avviaMode && (loading || showComplete || coachError)) {
    const isOverload = coachError?.type === 'overload';
    const exNames = exercisesInLastScheda.length > 0 ? exercisesInLastScheda : [];

    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: C.bg, fontFamily: FONT_BODY,
        padding: '0 32px',
      }}>
        {coachError ? (
          /* ── ERRORE ── */
          <>
            <div style={{
              width: 64, height: 64, borderRadius: 20,
              background: isOverload ? `${C.amber}14` : `${C.red}12`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20,
            }}>
              <BrainElectricity width={30} height={30} color={isOverload ? C.amber : C.red} strokeWidth={1.6} />
            </div>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>
                {isOverload ? 'Gemini è sovraccarico' : 'Qualcosa non ha funzionato'}
              </div>
              <div style={{ fontSize: 14, color: C.subtle, lineHeight: 1.6 }}>
                {isOverload
                  ? 'Il servizio AI ha troppo traffico in questo momento. Riprova tra qualche secondo.'
                  : 'Impossibile contattare il Coach AI. Controlla la connessione e riprova.'}
              </div>
            </div>
            <button onClick={() => { setCoachError(null); coachError.retry(); }} style={{
              padding: '13px 28px', borderRadius: 14, border: 'none', marginBottom: 10,
              background: C.blue, color: '#fff', fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700,
              cursor: 'pointer', boxShadow: `0 4px 16px ${C.blue}40`,
              WebkitTapHighlightColor: 'transparent',
            }}>Riprova</button>
            <button onClick={() => { setCoachError(null); setStartSessionName(null); setMessages([]); onBack?.(); }} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: FONT_BODY, fontSize: 14, color: C.subtle,
              WebkitTapHighlightColor: 'transparent', marginTop: 4,
            }}>Annulla</button>
          </>
        ) : showComplete ? (
          /* ── COMPLETAMENTO ── */
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, animation: 'coachCompleteIn 0.5s cubic-bezier(0.22,1,0.36,1) both' }}>
            <div style={{
              width: 72, height: 72, borderRadius: 24,
              background: `${C.green}14`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <circle cx="18" cy="18" r="16" stroke={C.green} strokeWidth="2" fill="none"
                  strokeDasharray="100" strokeDashoffset="0"
                  style={{ animation: 'coachCircleDraw 0.5s 0.1s cubic-bezier(0.22,1,0.36,1) both' }}
                />
                <polyline points="11,18 16,23 25,13" stroke={C.green} strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round" fill="none"
                  style={{ animation: 'coachCheckDraw 0.35s 0.5s cubic-bezier(0.22,1,0.36,1) both', strokeDasharray: 20, strokeDashoffset: 20 }}
                />
              </svg>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 4 }}>Analisi completata</div>
              <div style={{ fontSize: 14, color: C.subtle }}>Sto preparando il report...</div>
            </div>
          </div>
        ) : (
          /* ── LOADING ── */
          <AnalysisLoadingScreen exNames={exNames} schedaName={startSessionName} />
        )}

        <style>{`
          @keyframes coachCompleteIn {
            from { opacity: 0; transform: scale(0.85); }
            to   { opacity: 1; transform: scale(1); }
          }
          @keyframes coachCircleDraw {
            from { stroke-dashoffset: 100; }
            to   { stroke-dashoffset: 0; }
          }
          @keyframes coachCheckDraw {
            from { stroke-dashoffset: 20; }
            to   { stroke-dashoffset: 0; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      position: 'fixed', inset: 0,
      background: C.bg, fontFamily: FONT_BODY,
      paddingTop: 'max(56px, calc(env(safe-area-inset-top, 0px) + 14px))',
      paddingBottom: avviaMode
        ? 0
        : 'calc(env(safe-area-inset-bottom, 0px) + 130px)',
      animation: 'coachPageIn 0.4s cubic-bezier(0.25,0.46,0.45,0.94) both',
    }}>
      <style>{`@keyframes coachPageIn{from{opacity:0;transform:translateY(-16px)}to{opacity:1;transform:translateY(0)}}`}</style>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px 12px', flexShrink: 0, position: 'relative',
      }}>
        {avviaMode ? (
          <>
            <button
              onClick={() => { setStartSessionName(null); setMessages([]); onBack?.(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontFamily: FONT_BODY, fontSize: 16, fontWeight: 500, color: C.blue,
                WebkitTapHighlightColor: 'transparent',
              }}>
              <NavArrowLeft width={20} height={20} color={C.blue} strokeWidth={2} />
              Indietro
            </button>
            {startSessionName && (
              <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontSize: 16, fontWeight: 600, color: C.text, fontFamily: FONT_BODY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '50%' }}>
                {startSessionName}
              </div>
            )}
            {hasCoachReply && !loading && (
              <button
                onClick={() => { setMessages([]); handleChipSessionForScheda(startSessionName); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: 'rgba(120,120,128,0.10)', border: 'none',
                  borderRadius: 20, padding: '6px 12px',
                  cursor: 'pointer', fontFamily: FONT_BODY,
                  fontSize: 13, fontWeight: 600, color: C.subtle,
                  WebkitTapHighlightColor: 'transparent',
                }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M4 12a8 8 0 0 1 14.5-4.5M20 12a8 8 0 0 1-14.5 4.5" stroke={C.subtle} strokeWidth="2" strokeLinecap="round"/>
                  <path d="M18 4l.5 3.5L15 8" stroke={C.subtle} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 20l-.5-3.5L9 16" stroke={C.subtle} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Rigenera
              </button>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: `${C.blue}12`, border: `1px solid ${C.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BrainElectricity width={20} height={20} color={C.blue} strokeWidth={1.7} />
            </div>
            <div>
              <span style={{ fontSize: 18, fontWeight: 700, color: C.text, letterSpacing: -0.5, display: 'block', lineHeight: 1.15 }}>Coach AI</span>
              <span style={{ fontSize: 11, color: C.subtle, fontWeight: 500, letterSpacing: 0.2 }}>Analisi personalizzata</span>
            </div>
          </div>
        )}
        {messages.length > 0 && !avviaMode && (
          <button
            onClick={() => { setMessages([]); setExSubChips(null); }}
            style={{ background: 'rgba(120,120,128,0.12)', border: 'none', borderRadius: 999, width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Xmark width={14} height={14} color={C.subtle} />
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollContainerRef} style={{ flex: 1, overflowY: 'auto', paddingTop: 8, paddingBottom: 0, background: C.bg, overscrollBehavior: 'contain' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '52px 32px 0' }}>
            <div style={{ width: 52, height: 52, borderRadius: 16, background: `${C.blue}0D`, border: `1px solid ${C.blue}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <BrainElectricity width={26} height={26} color={C.blue} strokeWidth={1.6} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8, letterSpacing: -0.3, fontFamily: FONT_BODY }}>
              {noData ? 'Nessuna sessione' : 'Cosa vuoi sapere?'}
            </div>
            <div style={{ color: C.subtle, fontSize: 14, lineHeight: 1.65, fontFamily: FONT_BODY, maxWidth: 260, margin: '0 auto' }}>
              {noData
                ? 'Aggiungi almeno una sessione per usare il Coach AI.'
                : 'Seleziona una domanda rapida o scrivi direttamente al coach.'}
            </div>
          </div>
        )}
        {messages.map((m, i) => {
          // In avviaMode nascondi il bubble utente
          if (avviaMode && m.role === 'user') return null;
          return <Message key={i} role={m.role} text={m.text} charts={m.charts} sessions={db.sessions} schedaName={m.schedaName || lastSchedaName} />;
        })}
        {loading && !avviaMode && (
          <div style={{ padding: '0 16px 12px' }}>
            <div style={{ display: 'inline-flex', gap: 5, padding: '12px 14px', borderRadius: '18px 18px 18px 4px', background: 'rgba(120,120,128,0.10)' }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: 7, height: 7, borderRadius: '50%', background: C.subtle,
                  animation: `coachDot 1.2s ${i * 0.2}s ease-in-out infinite`,
                }} />
              ))}
            </div>
          </div>
        )}
        {coachError && !avviaMode && (
          <div style={{ margin: '0 16px 16px', padding: '14px 16px', borderRadius: 16, background: coachError.type === 'overload' ? `${C.amber}12` : `${C.red}10`, border: `1px solid ${coachError.type === 'overload' ? C.amber : C.red}30` }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>
              {coachError.type === 'overload' ? 'Gemini sovraccarico' : 'Errore di connessione'}
            </div>
            <div style={{ fontSize: 13, color: C.subtle, marginBottom: 12, lineHeight: 1.5 }}>
              {coachError.type === 'overload'
                ? 'Troppo traffico in questo momento. Riprova tra qualche secondo.'
                : 'Impossibile contattare il Coach AI. Controlla la connessione.'}
            </div>
            <button
              onClick={() => { setCoachError(null); coachError.retry(); }}
              style={{
                padding: '8px 18px', borderRadius: 10, border: 'none',
                background: C.blue, color: '#fff',
                fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
              }}>
              Riprova
            </button>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* CTA Avvia allenamento — in fondo al flex container, visibile dopo output Gemini */}
      {avviaMode && !loading && hasCoachReply && (
        <div style={{
          flexShrink: 0,
          padding: '12px 16px',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          background: C.bg,
          borderTop: '0.5px solid rgba(60,60,67,0.10)',
        }}>
          <button
            onClick={() => { onStartSession?.(startSessionName); onBack?.(); }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '100%', padding: '16px 20px',
              border: 'none', borderRadius: 18,
              background: C.blue, cursor: 'pointer',
              fontFamily: FONT_BODY, fontSize: 16, fontWeight: 700, color: '#fff',
              WebkitTapHighlightColor: 'transparent',
              boxShadow: `0 4px 16px ${C.blue}35`,
            }}
          >
            Avvia allenamento
          </button>
        </div>
      )}

      {/* Fixed bottom input area — nascosto in avviaMode */}
      {!avviaMode && <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 90px)',
        background: C.bg, borderTop: '0.5px solid rgba(60,60,67,0.12)',
        zIndex: 10,
      }}>
        {exSubChips && (
          <div style={{ padding: '10px 16px 4px', display: 'flex', gap: 8, overflowX: 'auto', flexWrap: 'nowrap' }}>
            <Chip label="Annulla" onClick={() => setExSubChips(null)} />
            {exSubChips.map(ex => (
              <Chip key={ex} label={ex} onClick={() => handleSelectExercise(ex)} disabled={loading} />
            ))}
          </div>
        )}
        {!exSubChips && (
          <div style={{ padding: '10px 16px 4px', display: 'flex', gap: 8, overflowX: 'auto', flexWrap: 'nowrap' }}>
            <Chip
              label={lastSchedaName ? `Analizza: ${lastSchedaName}` : 'Analizza ultima sessione'}
              onClick={handleChipSession}
              disabled={loading || noData}
            />
            <Chip label="Progressione esercizio" onClick={handleChipExercise} disabled={loading || noData} />
            <Chip label="Serve un deload?" onClick={handleChipDeload} disabled={loading || noData} />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px 12px' }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Scrivi una domanda..."
            disabled={loading}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 22, border: '1px solid rgba(120,120,128,0.22)',
              background: 'rgba(255,255,255,0.9)', fontSize: 14, fontFamily: FONT_BODY, color: C.text,
              outline: 'none', WebkitAppearance: 'none',
            }}
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            style={{
              width: 36, height: 36, borderRadius: 18, flexShrink: 0,
              background: loading || !input.trim() ? 'rgba(120,120,128,0.14)' : C.blue,
              border: 'none', cursor: loading || !input.trim() ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
            }}>
            <NavArrowUp width={16} height={16} color={loading || !input.trim() ? C.subtle : '#fff'} strokeWidth={2.2} />
          </button>
        </div>
      </div>}

      <style>{`
        @keyframes coachDot {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}
